import { useMutation, useQuery } from '@apollo/client';
import React from 'react';
import { useParams } from 'react-router-dom';
import { useAppDispatch } from '../../app/redux/useAppSelector';
import ConfirmationModal from '../../components/ConfirmationModal';
import { gql } from '../../graphql/generated/gql';
import { UIActions } from '../../ui/uiSlice';
import { legacyUpdateQueryList } from '../../utils/graphql/updateQueryList';
import useNavigate from '../../utils/router/useNavigate';
import AdminProvisionForm from './AdminProvisionForm';
import ProvisionDeletionConfirmMessage from './ProvisionDeletionConfirmMessage';

gql(`
  fragment AdminProvisionEditView_provision on Provision {
    ...AdminProvisionForm_provision
    ...ProvisionDeletionConfirmMessage_provision
  }
`);

const ADMIN_PROVISION_EDIT_VIEW_QUERY = gql(/** @complexity 24 */ `
  query AdminProvisionEditView {
    session: currentSession {
      organization {
        id
        provisions {
          ...AdminProvisionEditView_provision
        }
      }
    }
  }
`);

const DELETE_PROVISION_MUTATION = gql(/** @complexity 2 */ `
  mutation deleteProvision($id: ID!) {
    deleteProvision(id: $id) {
      id
    }
  }
`);

const UPDATE_PROVISION_MUTATION = gql(/** @complexity 22 */ `
  mutation updateProvision(
    $favoritePostIds: [ID!]
    $favoriteTopicIds: [ID!]
    $groupIds: [ID!]
    $id: ID!
    $name: String
    $postIds: [ID!]
    $topicIds: [ID!]
    $type: ProvisionType
    $urlEnabled: Boolean
  ) {
    updateProvision(
      id: $id
      name: $name
      favoritePostIds: $favoritePostIds
      favoriteTopicIds: $favoriteTopicIds
      groupIds: $groupIds
      postIds: $postIds
      topicIds: $topicIds
      type: $type
      urlEnabled: $urlEnabled
    ) {
      ...AdminProvisionEditView_provision
    }
  }
`);

const REGENERATE_PROVISION_SECRET_MUTATION = gql(/** @complexity 2 */ `
  mutation regenerateProvisionSecret($id: ID!) {
    regenerateProvisionSecret(id: $id) {
      id
      secret
    }
  }
`);

const AdminProvisionEditView = () => {
  const navigate = useNavigate();
  const { id } = useParams() as { id: string };
  const dispatch = useAppDispatch();

  const { data } = useQuery(ADMIN_PROVISION_EDIT_VIEW_QUERY);

  const provisions = data?.session?.organization?.provisions ?? [];
  const provision = provisions.find((p) => p?.id === id);

  const [deleteProvision] = useMutation(DELETE_PROVISION_MUTATION);
  const [regenerateProvisionSecret] = useMutation(
    REGENERATE_PROVISION_SECRET_MUTATION,
  );
  const [updateProvision] = useMutation(UPDATE_PROVISION_MUTATION);

  if (!provision) {
    return null;
  }

  const handleDelete = (onError: (e: Error) => unknown) => {
    const params = {
      title: 'Remove this provision?',
      message: <ProvisionDeletionConfirmMessage provision={provision} />,
      confirmButtonText: 'Delete',
      onConfirm: () =>
        deleteProvision({
          variables: { id },
          update: legacyUpdateQueryList(
            { query: ADMIN_PROVISION_EDIT_VIEW_QUERY },
            'session.organization.provisions',
            'deleteProvision',
            false,
          ),
        })
          .then(() => {
            navigate('/admin/provisions');
          })
          .catch((e: Error) => onError(e)),
    };

    dispatch(UIActions.openModal(ConfirmationModal, params));
  };

  const handleRegenerateUrl = () => {
    dispatch(
      UIActions.openModal(ConfirmationModal, {
        title: 'Regenerate link?',
        message:
          'Your current URL will no longer work and will be replaced by a new URL.',
        confirmButtonText: 'Regenerate',
        onConfirm: () => regenerateProvisionSecret({ variables: { id } }),
      }),
    );
  };

  return (
    <AdminProvisionForm
      provision={provision}
      onDelete={handleDelete}
      onRegenerateUrl={handleRegenerateUrl}
      onSubmit={(values) =>
        updateProvision({
          variables: {
            id,
            ...values,
          },
        })
      }
    />
  );
};

export default AdminProvisionEditView;
